import { Link } from 'react-router-dom';

interface PostItem {
  id: string;
  category: string;
  title: string;
  content: string;
  empathy_count: number;
  reply_count: number;
  created_at: string;
  profiles?: {
    nickname: string;
    user_type: string;
    partner_role?: string;
    avatar_url?: string;
  };
}

interface PendingPostsTabProps {
  posts: PostItem[];
  loading: boolean;
}

function getCategoryStyle(category: string) {
  switch (category) {
    case 'つぶやき・モヤモヤ':
      return 'bg-rose-100 text-rose-700';
    case '教えて！ケアの知恵':
      return 'bg-amber-100 text-amber-700';
    case '保育園・社会参加':
      return 'bg-emerald-100 text-emerald-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

export default function PendingPostsTab({ posts, loading }: PendingPostsTabProps) {
  const pendingPosts = posts.filter(p => (p as any).need_partner_response === true);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
        <div className="animate-spin w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full mx-auto mb-3"></div>
        <p className="text-gray-500 text-sm">読み込み中...</p>
      </div>
    );
  }

  if (pendingPosts.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 sm:p-12 text-center">
        <i className="ri-checkbox-circle-line text-4xl sm:text-5xl text-emerald-400 mb-3"></i>
        <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-1">すべて対応済みです</h3>
        <p className="text-gray-500 text-xs sm:text-sm">現在、回答待ちの投稿はありません</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {pendingPosts.map(post => (
        <div key={post.id} className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 hover:shadow-md transition-all">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-2 flex-wrap gap-1">
              <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${getCategoryStyle(post.category)}`}>
                {post.category}
              </span>
              <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium flex items-center space-x-1">
                <i className="ri-user-star-line text-xs"></i>
                <span>回答希望</span>
              </span>
            </div>
            <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
              {new Date(post.created_at).toLocaleDateString('ja-JP')}
            </span>
          </div>

          <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-1">{post.title}</h3>
          <p className="text-gray-500 text-xs sm:text-sm mb-3 line-clamp-2">{post.content}</p>

          <div className="flex items-center justify-between pt-3 border-t border-gray-100 gap-2">
            <div className="flex items-center space-x-2 sm:space-x-3 flex-wrap gap-1">
              <div className="flex items-center space-x-1 sm:space-x-2">
                {post.profiles?.avatar_url ? (
                  <img src={post.profiles.avatar_url} alt="" className="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover" />
                ) : (
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gray-200 flex items-center justify-center">
                    <i className="ri-user-smile-line text-gray-500 text-xs"></i>
                  </div>
                )}
                <span className="text-xs text-gray-600">{post.profiles?.nickname || '匿名'}</span>
              </div>
              <div className="flex items-center space-x-1 text-rose-400">
                <i className="ri-heart-3-line text-xs sm:text-sm"></i>
                <span className="text-xs">{post.empathy_count}</span>
              </div>
              <div className="flex items-center space-x-1 text-teal-500">
                <i className="ri-chat-3-line text-xs sm:text-sm"></i>
                <span className="text-xs">{post.reply_count}</span>
              </div>
            </div>
            <Link
              to={`/board/post/${post.id}`}
              className="px-3 sm:px-4 py-1.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg text-xs sm:text-sm hover:shadow-md transition-all cursor-pointer whitespace-nowrap"
            >
              回答する
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}
